import { useEffect, useRef, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "@api/useAuth";
import { LogoutButton, useRoleTest, ROLE_TEST_META } from "@common";
import {
  isAreaManager,
  isOrganizerOrAdmin,
  isPractitionerLead,
  isObserver,
  isPayroll,
  isBookkeeper,
  isFinanceCoordinator,
  getDefaultRouteForUser,
} from "@utils";

const PRODUCTION_HOST = "www.smilesanalytics.com";
const isProduction = window.location.hostname === PRODUCTION_HOST;

export default function AppHeader() {
  const { user, realUser } = useAuth();
  const { testRoleId, setTestRoleId } = useRoleTest();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    function onDocClick(e) {
      // Don't close dropdown if click is inside a modal/dialog
      if (e.target.closest('[role="dialog"]')) return;
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const linkBase = "px-3 py-2 rounded-lg text-sm font-semibold transition";
  const linkActive = "bg-palette-indigo-badge text-white";
  const linkIdle = "text-palette-gray-heading hover:bg-palette-indigo-soft";

  const initials = (user?.full_name || user?.email || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const testMeta = testRoleId ? ROLE_TEST_META[testRoleId] : null;

  return (
    <>
      {testMeta && (
        <div className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-palette-amber-indicator px-6 py-1.5 text-sm font-semibold text-palette-amber-primary">
          <span>
            🔧 Role Test Mode — viewing as <span className="font-bold">{testMeta.label}</span>
            {realUser?.full_name ? ` (you are ${realUser.full_name})` : ""}
          </span>
          <button
            type="button"
            onClick={() => {
              setTestRoleId(null);
              window.location.href = "/";
            }}
            className="rounded bg-palette-amber-primary/15 px-3 py-0.5 text-xs font-bold hover:bg-palette-amber-primary/25 transition"
          >
            ✕ Exit Role Test
          </button>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-palette-gray-subtle">
        <div className="mx-auto max-w-full px-6 h-14 flex items-center justify-between">
          {/* Left: sections */}
          <nav className="flex items-center gap-2">
            {/* Home — always first. Conditions are mutually exclusive by role,
                so only one Home link renders. */}
            {(isOrganizerOrAdmin(user) || isObserver(user)) && (
              <NavLink
                to="/admin/"
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Home
              </NavLink>
            )}
            {(isAreaManager(user) || isBookkeeper(user)) && (
              <NavLink
                to="/management/"
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Home
              </NavLink>
            )}
            {isPractitionerLead(user) && (
              <NavLink
                to={getDefaultRouteForUser(user)}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Home
              </NavLink>
            )}
            {/* Finance Operations — second */}
            {(isOrganizerOrAdmin(user) ||
              isAreaManager(user) ||
              isPractitionerLead(user) ||
              isObserver(user) ||
              isPayroll(user) ||
              isBookkeeper(user) ||
              isFinanceCoordinator(user)) && (
              <NavLink
                to="/finance-operations"
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Finance Operations
              </NavLink>
            )}
            {/* Cash Flow — third */}
            {(isOrganizerOrAdmin(user) ||
              isObserver(user) ||
              isPayroll(user) ||
              isBookkeeper(user) ||
              isFinanceCoordinator(user)) && (
              <NavLink
                to="/cash-flow"
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Cash Flow
              </NavLink>
            )}
            {/* Dens N Dente Cash Flow — fourth. Same roles as /cash-flow; keep the
                two conditions in sync. */}
            {(isOrganizerOrAdmin(user) ||
              isObserver(user) ||
              isPayroll(user) ||
              isBookkeeper(user) ||
              isFinanceCoordinator(user)) && (
              <NavLink
                to="/dens-n-dente-cash-flow"
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                Dens N Dente
              </NavLink>
            )}
            <span className="ml-1 rounded-md bg-palette-gray-subtle px-2 py-1 text-xs font-medium text-palette-gray-secondary">
              {isOrganizerOrAdmin(user)
                ? "Admin"
                : isAreaManager(user)
                  ? "Management"
                  : isPractitionerLead(user)
                    ? "Practitioner"
                    : isObserver(user)
                      ? "Observer"
                      : isPayroll(user)
                        ? "Payroll"
                        : isBookkeeper(user)
                          ? "Bookkeeper"
                          : isFinanceCoordinator(user)
                            ? "Finance Coordinator"
                            : ""}
            </span>
          </nav>

          {!isProduction && (
            <div className="absolute left-1/2 -translate-x-1/2 bg-palette-yellow-badge text-palette-yellow-primary text-sm font-bold py-1 px-4 rounded tracking-wide">
              ⚠ TESTING ENVIRONMENT ⚠
            </div>
          )}

          {/* Right: profile dropdown */}
          <div className="flex items-center ml-auto">
            {/* User menu */}
            <div className="relative">
              <button
                ref={btnRef}
                type="button"
                onClick={() => setOpen((s) => !s)}
                className="flex items-center gap-2 rounded-xl border border-palette-gray-divider bg-white px-3 py-1.5 text-sm font-medium text-palette-gray-text hover:bg-palette-gray-soft"
                aria-haspopup="menu"
                aria-expanded={open}
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-palette-indigo-badge text-white text-xs font-bold">
                  {initials}
                </span>
                <span className="max-w-[160px] truncate">
                  {user?.full_name || user?.email || "Account"}
                </span>
                <svg
                  className="h-4 w-4 text-palette-gray-secondary"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.116l3.71-3.885a.75.75 0 111.08 1.04l-4.24 4.44a.75.75 0 01-1.08 0l-4.24-4.44a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {open && (
                <div
                  ref={menuRef}
                  role="menu"
                  className="absolute right-0 mt-2 w-52 rounded-xl border border-palette-gray-divider bg-white shadow-lg overflow-hidden"
                >
                  <div className="px-4 py-3 text-sm">
                    <div className="font-semibold text-palette-gray-primary">
                      {user?.full_name || "User"}
                    </div>
                    <div className="text-palette-gray-secondary truncate">{user?.email}</div>
                  </div>
                  <div className="h-px bg-palette-gray-subtle" />
                  <MenuItem to="/profile" onClick={() => setOpen(false)}>
                    Profile
                  </MenuItem>
                  {/* If you want “Your Orders”, add the route and uncomment */}
                  {/* <MenuItem to="/orders" onClick={() => setOpen(false)}>Your Orders</MenuItem> */}
                  <div className="h-px bg-palette-gray-subtle" />
                  <div className="p-2">
                    <LogoutButton className="w-full justify-center" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

function MenuItem({ to, children, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-4 py-2.5 text-sm text-palette-gray-heading hover:bg-palette-gray-soft"
      role="menuitem"
    >
      {children}
    </Link>
  );
}
